import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';

export type ThinkingStatus = 'idle' | 'thinking' | 'complete' | 'error';

export type ThinkingPluginState = {
	/**
	 * Progress-bar facade target (0-100) chosen once per thinking session. Kept in
	 * state (rather than randomised per component mount) so all bars fill to the
	 * same value. `null` while idle.
	 */
	progressTarget: number | null;
	/**
	 * Epoch ms captured when the current thinking session started (status → 'thinking').
	 * Shared by every loading bar so their progress/rotation animations stay in sync
	 * across mount/unmount (e.g. switching between the editor bar and the sidebar bar).
	 * `null` while idle.
	 */
	startedAt: number | null;
	status: ThinkingStatus;
};

export type ThinkingPluginMeta = {
	status: ThinkingStatus;
};

// Facade progress hovers at a randomised target (85-95%) until completion.
const pickProgressTarget = (): number => 85 + Math.random() * 10;

export const thinkingPluginKey: PluginKey<ThinkingPluginState> = new PluginKey<ThinkingPluginState>(
	'aiSuggestionsThinkingPlugin',
);

export function createThinkingPlugin({
	dispatch,
}: {
	dispatch: Dispatch;
}): SafePlugin<ThinkingPluginState> {
	return new SafePlugin<ThinkingPluginState>({
		key: thinkingPluginKey,
		state: {
			init(): ThinkingPluginState {
				return {
					status: 'idle',
					startedAt: null,
					progressTarget: null,
				};
			},
			apply(tr, pluginState): ThinkingPluginState {
				const meta: ThinkingPluginMeta | undefined = tr.getMeta(thinkingPluginKey);

				if (meta && meta.status !== pluginState.status) {
					let startedAt = pluginState.startedAt;
					let progressTarget = pluginState.progressTarget;

					if (meta.status === 'thinking') {
						// Start of a new session: anchor the shared timing/target once.
						startedAt = Date.now();
						progressTarget = pickProgressTarget();
					} else if (meta.status === 'idle') {
						// Session ended/cancelled: clear so the next session starts fresh.
						startedAt = null;
						progressTarget = null;
					}
					// 'complete' and 'error' keep startedAt/progressTarget so the bar can
					// settle its progress before showing the ready / error state.

					const result: ThinkingPluginState = {
						status: meta.status,
						startedAt,
						progressTarget,
					};
					dispatch(thinkingPluginKey, result);
					return result;
				}

				return pluginState;
			},
		},
	});
}
